import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { getTheme, type ColorMode } from './index';
import { cssVarsForPreset } from './cssVars';
import { isKnownPreset, presetsByCategory } from './registry';
import { DEFAULT_PRESET_ID } from './tokens';
import {
  DENSITIES,
  DensityContext,
  type Density,
  type DensityContextValue,
  type DensityPreference,
} from './densityContext';
import {
  ColorModeContext,
  type ColorModeContextValue,
  type ColorModePreference,
} from './colorModeContext';

const STORAGE_KEY = 'camptain.color-mode';
const PRESET_STORAGE_KEY = 'camptain.theme-preset';
const DENSITY_STORAGE_KEY = 'camptain.density';
const PRESET_GROUPS = presetsByCategory();

function readPreference(): ColorModePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    /* localStorage unavailable — fall through */
  }
  return 'system';
}

function readPresetId(): string {
  try {
    const stored = localStorage.getItem(PRESET_STORAGE_KEY);
    if (isKnownPreset(stored)) return stored;
  } catch {
    /* localStorage unavailable — fall through */
  }
  return DEFAULT_PRESET_ID;
}

function readDensityPreference(): DensityPreference {
  try {
    const stored = localStorage.getItem(DENSITY_STORAGE_KEY);
    const match = DENSITIES.find((d) => d === stored);
    if (match) return match;
  } catch {
    /* localStorage unavailable — fall through */
  }
  return 'auto';
}

/**
 * Owns everything the MUI theme is built from — colour mode, theme preset and
 * information density — and persists each choice in localStorage.
 */
export function ColorModeProvider({ children }: { children: ReactNode }) {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const [preference, setPreferenceState] = useState<ColorModePreference>(readPreference);

  const setPreference = useCallback((next: ColorModePreference) => {
    setPreferenceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore persistence failure */
    }
  }, []);

  const [presetId, setPresetIdState] = useState<string>(readPresetId);

  const setPresetId = useCallback((next: string) => {
    const id = isKnownPreset(next) ? next : DEFAULT_PRESET_ID;
    setPresetIdState(id);
    try {
      localStorage.setItem(PRESET_STORAGE_KEY, id);
    } catch {
      /* ignore persistence failure */
    }
  }, []);

  const [densityPreference, setDensityPreferenceState] =
    useState<DensityPreference>(readDensityPreference);

  const setDensityPreference = useCallback((next: DensityPreference) => {
    setDensityPreferenceState(next);
    try {
      if (next === 'auto') localStorage.removeItem(DENSITY_STORAGE_KEY);
      else localStorage.setItem(DENSITY_STORAGE_KEY, next);
    } catch {
      /* ignore persistence failure */
    }
  }, []);

  // Auto: touch-friendly by default, compact on large (xl) workstation screens.
  const isWorkstation = useMediaQuery('(min-width:1536px)');
  const density: Density =
    densityPreference === 'auto' ? (isWorkstation ? 'compact' : 'comfortable') : densityPreference;

  const mode: ColorMode = preference === 'system' ? (prefersDark ? 'dark' : 'light') : preference;

  const toggle = useCallback(() => {
    setPreference(mode === 'light' ? 'dark' : 'light');
  }, [mode, setPreference]);

  useEffect(() => {
    document.documentElement.setAttribute('data-mui-color-scheme', mode);
  }, [mode]);

  // Exposed on <html> for plain-CSS hooks: [data-density='dense'] { … }
  useEffect(() => {
    document.documentElement.dataset.density = density;
  }, [density]);

  // Expose the preset's tokens as `--cf-*` custom properties for plain CSS.
  useEffect(() => {
    const root = document.documentElement.style;
    const vars = cssVarsForPreset(presetId, mode);
    for (const [name, value] of Object.entries(vars)) root.setProperty(name, value);
    return () => {
      for (const name of Object.keys(vars)) root.removeProperty(name);
    };
  }, [presetId, mode]);

  const theme = useMemo(() => getTheme(mode, presetId, density), [mode, presetId, density]);
  const value = useMemo<ColorModeContextValue>(
    () => ({
      mode,
      preference,
      setPreference,
      toggle,
      presetId,
      setPresetId,
      presets: PRESET_GROUPS,
    }),
    [mode, preference, setPreference, toggle, presetId, setPresetId],
  );
  const densityValue = useMemo<DensityContextValue>(
    () => ({ density, preference: densityPreference, setPreference: setDensityPreference }),
    [density, densityPreference, setDensityPreference],
  );

  return (
    <ColorModeContext value={value}>
      <DensityContext value={densityValue}>
        <ThemeProvider theme={theme} defaultMode={mode}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </DensityContext>
    </ColorModeContext>
  );
}
